 
import { useEffect, useState, useMemo, useRef } from "react";
import api from "../api/api";
import "../components/table.css";
import CancelIcon from "@mui/icons-material/Cancel";
import Pagination from "../components/Pagination";
import BackButton from "../components/BackButton";
import Badge from "../components/Badge";
import usePagination from "../utils/usePagination";
import Table from "../components/table";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import { Link } from "react-router-dom";
import DatePicker from "react-datepicker";
import Button from "../components/Button";
 
const ListWhAdmin = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
 
  const [search, setSearch] = useState("");
 
  // DATE RANGE
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [open, setOpen] = useState(false);
 
  // DROPDOWN FILTER
  const [selectedUser, setSelectedUser] = useState("");
  const [createdByList, setCreatedByList] = useState([]);
 
  const inputRef = useRef(null);
 
  // FETCH DATA
  useEffect(() => {
    const fetchEnquiries = async () => {
      try {
        const response = await api.get("/warehouse/all-requests");
        setEnquiries(response.data.requests || []);
      } catch (error) {
        console.error("Failed to fetch warehouse:", error);
        setEnquiries([]);
      } finally {
        setLoading(false);
      }
    };
 
    fetchEnquiries();
  }, []);
 
  // FIXED: TEMP WAREHOUSE LIST (correct structure)
  useEffect(() => {
    setCreatedByList([
      { user_id: 1, full_name: "MetroCargo Warehouse - Pending" },
      { user_id: 2, full_name: "SwiftPort Logistics Park - Booked" },
    ]);
  }, []);
 
  // FILTER DATA (SEARCH + DATE + USER)
  const filteredEnquiries = useMemo(() => {
    return enquiries.filter((item) => {
      const matchSearch = item?.customer_name
        ?.toLowerCase()
        ?.includes(search.toLowerCase());
 
      const matchUser = selectedUser
        ? item?.warehouse_name === selectedUser
        : true;
 
      const itemDate = new Date(item.start_date);
 
      const matchDate =
        startDate && endDate
          ? itemDate >= startDate && itemDate <= endDate
          : true;
 
      return matchSearch && matchUser && matchDate;
    });
  }, [enquiries, search, selectedUser, startDate, endDate]);
 
  // PAGINATION
 const {
  currentPage,
  itemsPerPage,
  totalPages,
  totalItems,
  currentItems,
  goToPage,
  handleItemsPerPageChange,
} = usePagination(filteredEnquiries, 5);
 
  // FILTER ACTIONS
  const handleFilter = () => {
    console.log("Filter applied");
  };
 
  const handleClearFilter = () => {
    setDateRange([null, null]);
    setSelectedUser("");
    setSearch("");
    setOpen(false);
  };
 
  const handleToggle = () => setOpen((prev) => !prev);
 
  // TABLE COLUMNS
  const columns = [
    { label: "Request ID", accessor: "warehouse_request_id" },
    { label: "Customer Name", accessor: "customer_name" },
    { label: "Product Name", accessor: "product_name" },
    { label: "Start Date", accessor: "start_date" },
    { label: "End Date", accessor: "end_date" },
    { label: "Warehouse", accessor: "warehouse_name" },
    {
      label: "Status",
      render: (wh) => (
        <>
          {wh.availability_status?.toLowerCase() === "pending" && (
            <Badge text="Pending" className="gray" />
          )}
          {wh.availability_status?.toLowerCase() === "available" && (
            <Badge text="Available" className="gray" />
          )}
          {wh.availability_status?.toLowerCase() === "booked" && (
            <Badge text="Booked" className="gray" />
          )}
        </>
      ),
    },
      {
        label: "Action",
        render: (row) => (
          <Link to={`/edit-warehouse-requests/${row.warehouse_request_id}`}>
            <EditIcon style={{ color: "#1976d2", cursor: "pointer" }} />
          </Link>
        ),
      },
  ];
 
  return (
    <>
      {loading ? (
        <p>Loading Warehouse...</p>
      ) : (
        <>
          <BackButton />
 
          {/* FILTER SECTION */}
          <div className="row align-items-center mb-4 flex-wrap">
 
            {/* DATE PICKER */}
            <div className="col-sm-6 col-md-3">
              <input
                ref={inputRef}
                readOnly
                value={
                  startDate && endDate
                    ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
                    : ""
                }
                onClick={() => setOpen(!open)}
                placeholder="Select Date Range"
                className="form-control"
              />
 
              {open && (
                <div className="position-absolute bg-white shadow p-3 rounded mt-1 z-3"
                >
                  <DatePicker
                    inline
                    selectsRange
                    monthsShown={2}
                    startDate={startDate}
                    endDate={endDate}
                    onChange={(update) => setDateRange(update)}
                    maxDate={new Date()}
                  />
 
                  <div className="d-flex justify-content-end mt-2">
                    <Button onClick={handleToggle} text="Done" btncls="me-2" />
                    <Button
                      onClick={handleClearFilter}
                      text="Clear"
                      variant="danger"
                    />
                  </div>
                </div>
              )}
            </div>
 
            {/* DROPDOWN */}
            <div className="col-md-4 col-lg-3 mt-3 mt-md-0">
              <select
                className="form-select custom-select"
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
              >
                <option value="">Select Warehouse</option>
                {(createdByList || []).map((usr) => (
                  <option key={usr.user_id} value={usr.full_name}>
                    {usr.full_name}
                  </option>
                ))}
              </select>
            </div>
 
            {/* BUTTONS */}
            <div className="col-sm-6 col-md-4 col-lg-4 d-flex mt-3 mt-sm-0 align-items-center gap-2">
              <Button
                onClick={handleFilter}
                text="Apply Filter"
                btncls="me-2 mt-0 w-100 w-sm-auto"
              />
              <Button
                onClick={handleClearFilter}
                text="Clear Filter"
                variant="danger"
                btncls="mt-0 w-100 w-sm-auto light-red"
              />
            </div>
          </div>
 
          {/* SEARCH */}
          <div className="d-flex align-items-center gap-2">
            <div className="mb-3 position-relative flex-grow-1">
              <input
                type="search"
                className="form-control ps-5"
                placeholder="Search by Company Name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
 
              <SearchIcon
                style={{
                  position: "absolute",
                  left: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#6b7280",
                  width: "16px",
                  height: "16px",
                }}
              />
            </div>
          </div>
 
          {/* TABLE */}
          <Table
            columns={columns}
            data={currentItems}
            loading={loading}
            emptyMessage="No Warehouse List Found"
            itemsPerPage={itemsPerPage}
            pagination={
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={goToPage}
                itemsPerPage={itemsPerPage}
                onItemsPerPageChange={handleItemsPerPageChange}
                totalItems={totalItems}
              />
            }
          />
        </>
      )}
    </>
  );
};
 
export default ListWhAdmin;
 